/**
 * Data for the annotated product-page demo — ten on-page items you control,
 * each pinned to a region of the mock browser window. Copy derives from the
 * source deck's bullets: everything YOU control on the URL.
 * Index 0-8 map to number keys 1-9; index 9 maps to key 0.
 */

export type HotspotId =
  | "title-tag"
  | "meta-description"
  | "url"
  | "h1"
  | "headings"
  | "copy"
  | "alt-lazy"
  | "internal-links"
  | "schema"
  | "vitals";

/** Extra panel content rendered alongside the body copy. */
export type HotspotExtra = "serpPreview" | "altCode" | "schemaCode";

export interface HotspotInfo {
  id: HotspotId;
  n: number;
  title: string;
  body: string;
  extra?: HotspotExtra;
}

export const HOTSPOTS: readonly HotspotInfo[] = [
  {
    id: "title-tag",
    n: 1,
    title: "Title tag",
    body: "Everything you control on the URL starts here. The title lives in the browser tab and becomes the blue link on Google — keep it unique per page.",
  },
  {
    id: "meta-description",
    n: 2,
    title: "Meta description",
    body: "Your summary under the blue link — another piece you fully control. Like the title, keep it unique per page.",
    extra: "serpPreview",
  },
  {
    id: "url",
    n: 3,
    title: "Descriptive URL",
    body: "Descriptive URLs where you can: words and hyphens in a logical path, so people and crawlers can read what the page is about.",
  },
  {
    id: "h1",
    n: 4,
    title: "One clear H1",
    body: "One primary topic per page, one clear H1. It should match the page's main promise — here: a stability shoe built for flat feet.",
  },
  {
    id: "headings",
    n: 5,
    title: "H2–H3 structure",
    body: "Use H2–H3 to structure sections for users and crawlers: one H1 on top, subheads carrying the outline underneath.",
  },
  {
    id: "copy",
    n: 6,
    title: "Body copy",
    body: "Align one primary topic with the words people actually search. Avoid keyword stuffing — the copy has to read naturally first.",
  },
  {
    id: "alt-lazy",
    n: 7,
    title: "Image alt + fetchpriority",
    body: "Meaningful file names and alt text where images carry information; use fetchpriority high for important images.",
    extra: "altCode",
  },
  {
    id: "internal-links",
    n: 8,
    title: "Internal links",
    body: "Internal links from related pages, with helpful anchor text — varied, not spammy. 'Running shoe fitting guide' beats 'click here'.",
  },
  {
    id: "schema",
    n: 9,
    title: "Structured data",
    body: "Structured data — when appropriate. This page marks up its price and rating as a Product in JSON-LD:",
    extra: "schemaCode",
  },
  {
    id: "vitals",
    n: 10,
    title: "Fast core page",
    body: "Core page fast on mobile: no blocking huge assets in the first screen. The vitals badge here is invented demo data — measure your own.",
  },
];

export function hotspotById(id: HotspotId): HotspotInfo {
  const info = HOTSPOTS.find((h) => h.id === id);
  if (!info) throw new Error(`Unknown hotspot: ${id}`);
  return info;
}

export const ALT_CODE = `<img
  src="stability-runner-side.jpg"
  alt="Side profile of a stability
       running shoe with arch support"
  fetchpriority="high"
/>`;

export const SCHEMA_CODE = `{
  "@context": "https://schema.org",
  "@type": "Product",
  "name": "Stability Runner",
  "offers": { "price": "129.00" },
  "aggregateRating": {
    "ratingValue": "4.6"
  }
}`;
